import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import './CheckoutSuccess.css';

export default function CheckoutSuccess({ refreshUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }

    // Poll payment status
    const pollStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${API}/subscription/checkout-status/${sessionId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.payment_status === 'paid') {
          // Payment successful - refresh user data from parent
          try {
            if (refreshUser) {
              await refreshUser();
              console.log('User data refreshed from parent component');
            } else {
              // Fallback: direct API call
              const userResponse = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              localStorage.setItem('user', JSON.stringify(userResponse.data));
              console.log('User upgraded to Pro:', userResponse.data);
            }
          } catch (error) {
            console.error('Failed to refresh user data:', error);
          }
          
          setStatus('success');
          return true;
        }

        return false;
      } catch (error) {
        console.error('Error checking status:', error);
        return false;
      }
    };

    const intervalId = setInterval(async () => {
      if (attempts >= maxAttempts) {
        clearInterval(intervalId);
        setStatus('timeout');
        return;
      }

      const completed = await pollStatus();
      if (completed) {
        clearInterval(intervalId);
      } else {
        setAttempts(prev => prev + 1);
      }
    }, 2000);

    // Initial check
    pollStatus().then(completed => {
      if (completed) {
        clearInterval(intervalId);
      }
    });

    return () => clearInterval(intervalId);
  }, [searchParams, attempts]);

  if (status === 'checking') {
    return (
      <div className="checkout-success-page">
        <div className="status-card checking">
          <div className="spinner-large"></div>
          <h1>Processing Your Payment...</h1>
          <p>Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="checkout-success-page">
        <div className="status-card success">
          <div className="success-icon">✓</div>
          <h1>Welcome to Pro!</h1>
          <p>Your subscription has been activated successfully.</p>
          <ul className="benefits-list">
            <li>✓ 4 stories per month</li>
            <li>✓ Premium illustrations</li>
            <li>✓ Print and Send to Kindle</li>
          </ul>
          <Button 
            onClick={() => {
              // Force full page reload to ensure user state is refreshed
              window.location.href = '/dashboard';
            }}
            className="continue-button"
          >
            Start Creating Stories →
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'timeout') {
    return (
      <div className="checkout-success-page">
        <div className="status-card warning">
          <div className="warning-icon">⏱</div>
          <h1>Payment Processing</h1>
          <p>Your payment is still being processed. This may take a few minutes.</p>
          <p>Please check your email for confirmation, or contact support if you have any issues.</p>
          <Button 
            onClick={() => window.location.href = '/dashboard'}
            className="continue-button"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-success-page">
      <div className="status-card error">
        <div className="error-icon">✗</div>
        <h1>Payment Failed</h1>
        <p>There was an issue processing your payment.</p>
        <p>Please try again or contact support.</p>
        <Button 
          onClick={() => window.location.href = '/pricing'}
          className="continue-button"
        >
          Try Again
        </Button>
      </div>
    </div>
  );
}
